import { useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Boxes, AlertTriangle, Check, Package, Layers } from "lucide-react";
import { ExportMenu } from "@/components/ExportMenu";
import { useSortable, SortableTh } from "@/components/SortableTh";

const LOW_STOCK = 5;

const bucket = (stock) => (stock <= 0 ? "out" : stock <= LOW_STOCK ? "low" : "in");

export const InventoryManager = ({ products = [], format, search = "", onUpdated }) => {
  const [tab, setTab] = useState("all");
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(null);

  const totals = useMemo(() => {
    const units = products.reduce((s, p) => s + (p.stock || 0), 0);
    const value = products.reduce((s, p) => s + (p.stock || 0) * (p.price || 0), 0);
    const out = products.filter((p) => (p.stock || 0) <= 0).length;
    const low = products.filter((p) => (p.stock || 0) > 0 && (p.stock || 0) <= LOW_STOCK).length;
    return { units, value, out, low };
  }, [products]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products
      .filter((p) => tab === "all" || bucket(p.stock || 0) === tab)
      .filter((p) => !q || (p.title || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q));
  }, [products, tab, search]);

  const { sorted, sortKey, dir, toggle } = useSortable(filtered, {
    title: (p) => p.title || "",
    sku: (p) => p.sku || "",
    price: (p) => p.price ?? 0,
    stock: (p) => p.stock ?? 0,
  });

  const saveStock = async (p) => {
    const val = drafts[p.product_id];
    if (val === undefined || val === "" || Number(val) === p.stock) return;
    setSaving(p.product_id);
    try {
      await api.patch(`/seller/products/${p.product_id}/stock`, { stock: Math.max(0, parseInt(val, 10) || 0) });
      toast.success("Stock updated");
      setDrafts((d) => { const n = { ...d }; delete n[p.product_id]; return n; });
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update stock");
    } finally {
      setSaving(null);
    }
  };

  const kpis = [
    { icon: Layers, label: "SKUs", value: products.length, color: "text-primary" },
    { icon: Boxes, label: "Units in stock", value: totals.units, color: "text-secondary" },
    { icon: Package, label: "Inventory value", value: format(totals.value), color: "text-green-600" },
    { icon: AlertTriangle, label: "Need restock", value: totals.out + totals.low, color: "text-amber-600" },
  ];

  const TABS = [
    { key: "all", label: "All" },
    { key: "in", label: "In stock" },
    { key: "low", label: `Low (${totals.low})` },
    { key: "out", label: `Out (${totals.out})` },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} className="p-4" data-testid={`inv-kpi-${k.label}`}>
            <k.icon className={`w-5 h-5 ${k.color} mb-2`} />
            <p className="text-2xl font-black font-display">{k.value}</p>
            <p className="text-xs text-muted-foreground">{k.label}</p>
          </Card>
        ))}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="inventory-tabs">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`inv-tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-end -mt-2">
        <ExportMenu filename="inventory" title="Inventory" testid="inventory-export"
          columns={[
            { header: "Product", accessor: (p) => p.title },
            { header: "SKU", accessor: (p) => p.sku || "" },
            { header: "Price (EUR)", accessor: (p) => (p.price ?? 0).toFixed(2) },
            { header: "Stock", accessor: (p) => p.stock ?? 0 },
            { header: "Status", accessor: (p) => bucket(p.stock || 0) },
          ]}
          rows={filtered} />
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTh label="Product" sortKey="title" activeKey={sortKey} dir={dir} onSort={toggle} />
              <SortableTh label="SKU" sortKey="sku" activeKey={sortKey} dir={dir} onSort={toggle} className="hidden sm:table-cell" />
              <SortableTh label="Price" sortKey="price" activeKey={sortKey} dir={dir} onSort={toggle} />
              <SortableTh label="Current" sortKey="stock" activeKey={sortKey} dir={dir} onSort={toggle} />
              <TableHead>Update stock</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((p) => {
              const b = bucket(p.stock || 0);
              const draft = drafts[p.product_id];
              const dirty = draft !== undefined && draft !== "" && Number(draft) !== p.stock;
              return (
                <TableRow key={p.product_id} data-testid={`inv-row-${p.product_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-muted shrink-0 flex items-center justify-center">
                        {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <span className="font-medium text-sm truncate max-w-[200px]">{p.title}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-xs text-muted-foreground font-mono">{p.sku || "—"}</TableCell>
                  <TableCell className="text-sm font-semibold">{format(p.price)}</TableCell>
                  <TableCell>
                    <Badge className={b === "out" ? "bg-red-500" : b === "low" ? "bg-amber-500" : "bg-green-600"}>
                      {b === "out" ? "Out" : `${p.stock}`}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number" min="0"
                        data-testid={`inv-stock-input-${p.product_id}`}
                        className="h-9 w-24"
                        value={draft ?? p.stock ?? 0}
                        onChange={(e) => setDrafts((d) => ({ ...d, [p.product_id]: e.target.value }))}
                      />
                      <Button size="icon" className="rounded-full h-9 w-9 shrink-0" disabled={!dirty || saving === p.product_id}
                        data-testid={`inv-save-${p.product_id}`} onClick={() => saveStock(p)}>
                        <Check className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                <Boxes className="w-10 h-10 mx-auto mb-3 opacity-40" />
                {products.length === 0 ? "No products to track yet." : "No products in this view."}
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
